import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Card,
  Button,
  Dialog,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateEnumMutation,
  useDeleteEnumMutation,
  useGetallEnumSearchQuery,
} from "../../../Api/attoDeskApi";
import {
  CustomerOptionString,
  IEnumType,
} from "../../../Api/Interface/api.interface";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { EnumValuType } from "../../../Core/Enum/enum";

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IEnumType;
  enum_type?: string;
}

const Team = ({
  openModel = false,
  handleCloseDialog,
  data,
  enum_type,
}: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [mode, setMode] = React.useState<"edit" | "delete">("edit");
  // Country selected while creating a city; used only to filter the province list.
  // The city itself is stored against the chosen province (parent_id).
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);

  const [newCompany, { isLoading }] = useCreateEnumMutation();
  const isMobileRes = useIsMobile();

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const formik = useFormik<IEnumType>({
    initialValues: {
      id: Number(data?.id),
      enum_value: data?.enum_value || "",
      enum_type: data?.enum_type || enum_type || "",
      parent_id: data?.parent_id ?? null,
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          ...(data?.id ? { id: Number(values.id) } : {}),
          enum_value: toProperCase(values.enum_value),
          enum_type: values.enum_type,
          parent_id: values.parent_id ?? null,
        };

        const response = await newCompany(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const [deleteCategory] = useDeleteEnumMutation();
  const handleDelete = async () => {
    try {
      if (data) {
        const response = await deleteCategory(data?.id).unwrap();
        if (response.status) {
          showMessage(response.message);
          handleClose();
        } else {
          showErrorMessage(response.message);
        }
      }
    } catch (error) {
      showErrorMessage("Failed to delete the enum");
    }
  };

  const reminderBeforOptions: CustomerOptionString[] = Object.values(
    EnumValuType
  ).map((day) => ({
    label: day,
    value: day,
  }));

  const currentType = String(formik.values.enum_type);
  const needsCountry =
    currentType === EnumValuType.province || currentType === EnumValuType.city;
  const needsProvince = currentType === EnumValuType.city;

  // Load parent options only when relevant.
  const { data: countryData } = useGetallEnumSearchQuery(
    { searchText: "", enum_type: EnumValuType.country },
    { skip: !needsCountry }
  );
  const { data: provinceData } = useGetallEnumSearchQuery(
    { searchText: "", enum_type: EnumValuType.province },
    { skip: !needsProvince }
  );

  const countryList: IEnumType[] = Array.isArray(countryData?.data)
    ? (countryData?.data as IEnumType[])
    : [];
  const provinceList: IEnumType[] = Array.isArray(provinceData?.data)
    ? (provinceData?.data as IEnumType[])
    : [];

  // When editing a city, derive its country from the parent province so the
  // country dropdown is pre-filled and the province list is filtered correctly.
  useEffect(() => {
    if (needsProvince && data?.parent_id && provinceList.length > 0) {
      const parentProvince = provinceList.find((p) => p.id === data.parent_id);
      if (parentProvince?.parent_id) {
        setSelectedCountryId(Number(parentProvince.parent_id));
      }
    }
  }, [needsProvince, data?.parent_id, provinceList]);

  const filteredProvinces = useMemo(
    () =>
      provinceList.filter(
        (p) => Number(p.parent_id) === Number(selectedCountryId)
      ),
    [provinceList, selectedCountryId]
  );

  const formValid = useMemo(() => {
    if (
      !formik.values.enum_type ||
      !formik.values.enum_value
    ) {
      return false;
    }
    // Province needs a country; city needs a province (which implies a country).
    if (needsCountry && !formik.values.parent_id) {
      return false;
    }
    return true;
  }, [formik.values, needsCountry]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="md"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={1}
              sx={{ padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid item pt={2} alignContent={"center"}>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(e, value) => value && setMode(value)}
                  size="small"
                  orientation="horizontal"
                  sx={{
                    flexWrap: { xs: "wrap", md: "nowrap" },
                    width: { xs: "100%", md: "auto" },
                    justifyContent: {
                      xs: "flex-start",
                      md: "flex-end",
                    },
                  }}
                >
                  {data && (
                    <ToggleButton
                      value="edit"
                      color="primary"
                      sx={{
                        textTransform: "none",
                        gap: 1,
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      {data ? "Edit" : "Create"}
                    </ToggleButton>
                  )}
                  {data && (
                    <ToggleButton
                      value="delete"
                      color="primary"
                      sx={{
                        textTransform: "none",
                        gap: 1,
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      Delete
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Grid>
              <Grid
                item
                pt={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  className={classes.editButton}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {mode === "edit" && (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Card className={classes.card}>
                        <Grid container spacing={3} p={2}>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Type
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Autocomplete
                                  options={reminderBeforOptions}
                                  getOptionLabel={(
                                    option: CustomerOptionString
                                  ) => option.label}
                                  value={
                                    reminderBeforOptions.find(
                                      (opt) =>
                                        opt.value ===
                                        String(formik.values.enum_type)
                                    ) || null
                                  }
                                  onChange={(
                                    _,
                                    newValue: CustomerOptionString | null
                                  ) => {
                                    formik.setFieldValue(
                                      "enum_type",
                                      newValue?.value || ""
                                    );
                                    // Parent depends on type, so clear it on change.
                                    formik.setFieldValue("parent_id", null);
                                    setSelectedCountryId(null);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Type"
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                      className="textField"
                                      InputProps={{
                                        ...params.InputProps,
                                      }}
                                    />
                                  )}
                                  sx={{ width: "100%" }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          {needsCountry && (
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Country
                                    <span className={classes.requiedfield}>
                                      *
                                    </span>
                                  </Typography>
                                </Grid>
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Autocomplete
                                    options={countryList}
                                    getOptionLabel={(option: IEnumType) =>
                                      option.enum_value
                                    }
                                    value={
                                      needsProvince
                                        ? countryList.find(
                                            (c) => c.id === selectedCountryId
                                          ) || null
                                        : countryList.find(
                                            (c) =>
                                              c.id === formik.values.parent_id
                                          ) || null
                                    }
                                    onChange={(_, newValue: IEnumType | null) => {
                                      if (needsProvince) {
                                        // City: country only filters provinces.
                                        setSelectedCountryId(
                                          newValue?.id ?? null
                                        );
                                        formik.setFieldValue("parent_id", null);
                                      } else {
                                        // Province: country is the parent.
                                        formik.setFieldValue(
                                          "parent_id",
                                          newValue?.id ?? null
                                        );
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Select Country"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        className="textField"
                                      />
                                    )}
                                    sx={{ width: "100%" }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          )}

                          {needsProvince && (
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container alignItems="center">
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Typography variant="subtitle1">
                                    Province
                                    <span className={classes.requiedfield}>
                                      *
                                    </span>
                                  </Typography>
                                </Grid>
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                  <Autocomplete
                                    options={filteredProvinces}
                                    disabled={!selectedCountryId}
                                    getOptionLabel={(option: IEnumType) =>
                                      option.enum_value
                                    }
                                    value={
                                      filteredProvinces.find(
                                        (p) => p.id === formik.values.parent_id
                                      ) || null
                                    }
                                    onChange={(_, newValue: IEnumType | null) => {
                                      formik.setFieldValue(
                                        "parent_id",
                                        newValue?.id ?? null
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder={
                                          selectedCountryId
                                            ? "Select Province"
                                            : "Select a country first"
                                        }
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        className="textField"
                                      />
                                    )}
                                    sx={{ width: "100%" }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          )}

                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Name
                                  <span className={classes.requiedfield}>
                                    *
                                  </span>
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <TextField
                                  placeholder="Name"
                                  size="small"
                                  {...formik.getFieldProps("enum_value")}
                                  className={classes.textField}
                                  InputProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid
                        container
                        spacing={1}
                        justifyContent={"space-between"}
                      >
                        <Grid item>
                          <Button
                            type="submit"
                            variant="contained"
                            className={classes.addButton}
                            disabled={!formValid || isLoading}
                          >
                            {isLoading && (
                              <CircularProgress
                                size={18}
                                className={classes.isLoading}
                                sx={{ marginRight: 1 }}
                              />
                            )}
                            {data ? "Update" : "Create"}
                          </Button>
                        </Grid>
                        {/* {data && (
                          <Grid
                            item
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            <Button
                              variant="outlined"
                              color="error"
                              className={classes.addButton}
                              onClick={handleDelete}
                            >
                              Delete
                            </Button>
                          </Grid>
                        )} */}
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </FormikProvider>
            </Grid>
          )}

          {mode === "delete" && data && (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Card
                        className={classes.card}
                        sx={{ minHeight: "200px" }}
                      >
                        <Grid container spacing={3} p={2}>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Are you sure you want to delete "{data.enum_value}
                              "?
                            </Typography>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid
                        container
                        spacing={1}
                        justifyContent={"space-between"}
                      >
                        {data && (
                          <Grid
                            item
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            <Button
                              variant="outlined"
                              color="error"
                              className={classes.addButton}
                              onClick={handleDelete}
                            >
                              Delete
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </FormikProvider>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
